import "./Overview.css";

import { EditGreyIcon } from "../../../../../assets";
import { EditDatesPanel } from "../../../../../templates";
import { hasStandardSpecificDefinition } from "../../../../../utils";
import { ActionButtons, Column, ErrorBox, Gallery, PhaseLifecycle, Row, Select } from "../../../../../widget";
import { GenericModal } from "../../../../developer/components";
import {
  BadgesSection,
  BasicDetailsSection,
  CertificationsSection,
  EditableBasic,
  StandardSpecificSection,
} from "../../../../shared/projects/project/overview/components";
import { useOverview } from "./useOverview";

const Overview = (): JSX.Element => {
  const {
    images,
    videoUrl,
    longDescription,
    currentUserType,
    projectDetails,
    publicProjectDetails,
    projectDates,
    setProjectDates,
    errors,
    isEditable,
    isReadOnly,
    setIsReadOnly,
    isNoStandard,
    standardOptions,
    selectedStandardUuid,
    showStandardChangeModal,
    onStandardSelect,
    onCancelStandardChange,
    basicDetailsData,
    setBasicDetailsData,
    locationData,
    setLocationData,
    onCancel,
    onSubmit,
    validatorData,
    certificateData,
    badgeData,
    standardSpecificDefinition,
    isOnSubmitLoading,
    showUpdateGroupVVBModal,
    setShowUpdateGroupVVBModal,
    showEditDatesPanel,
    setShowEditDatesPanel,
    shouldRefreshProjectDetails,
  } = useOverview();

  return (
    <>
      <div className="ProjectTabSection">
        <Row spacingV="ml">
          <Column span={12}>
            <h2>About the project</h2>
          </Column>
        </Row>
        {longDescription && (
          <Row spacingV="xl">
            <Column span={12}>
              <p className="body1">{longDescription}</p>
            </Column>
          </Row>
        )}
        {images && images.length > 0 && (
          <Row spacingV="ll">
            <Column span={12}>
              <Gallery imageSources={images} videoUrl={videoUrl} corners="rounded" />
            </Column>
          </Row>
        )}
        <Row spacingV="ml">
          <Column span={6}>
            <h2>Details</h2>
          </Column>
          <Column span={6} justify="between">
            {isEditable && isReadOnly && (
              <div className="editOverviewButton">
                <EditGreyIcon onClick={() => setIsReadOnly(false)} />
              </div>
            )}
          </Column>
        </Row>
        {errors && errors.length > 0 && (
          <Row spacingV="ml">
            <Column span={4}>
              <ErrorBox
                title="Please solve the below issues before saving"
                text={errors.map((error) => error.message)}
              />
            </Column>
          </Row>
        )}
        <Row>
          <Column span={12}>
            <form onSubmit={onSubmit}>
              {isNoStandard && isEditable && !isReadOnly && (
                <Row spacingV="ll">
                  <Column span={4}>
                    <Select
                      label="Standard"
                      placeholder="Select"
                      data={standardOptions}
                      value={selectedStandardUuid}
                      onChange={onStandardSelect}
                      corners="rounded"
                    />
                  </Column>
                </Row>
              )}
              {projectDetails && !isReadOnly ? (
                <EditableBasic
                  projectDetails={projectDetails}
                  basicDetailsData={basicDetailsData}
                  setBasicDetailsData={setBasicDetailsData}
                  locationData={locationData}
                  setLocationData={setLocationData}
                  validatorData={validatorData}
                  isNoStandard={isNoStandard}
                />
              ) : (
                (projectDetails || publicProjectDetails) && (
                  <BasicDetailsSection
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    projectDetails={(projectDetails || publicProjectDetails)!}
                    basicDetailsData={basicDetailsData}
                    setBasicDetailsData={setBasicDetailsData}
                    locationData={locationData}
                    isReadOnly={isReadOnly}
                    validatorData={validatorData}
                    isNoStandard={isNoStandard}
                  />
                )
              )}
              {certificateData.length > 0 && <CertificationsSection certificates={certificateData} />}
              {badgeData.length > 0 && <BadgesSection badges={badgeData} />}
              {!isReadOnly && (
                <Row className="OverviewActionButtons">
                  <Column span={2}>
                    <ActionButtons onCancel={onCancel} isLoading={isOnSubmitLoading} />
                  </Column>
                </Row>
              )}
            </form>
          </Column>
        </Row>
      </div>

      {projectDates && (
        <div className="ProjectTabSection">
          <Row spacingV="ml">
            <Column span={12}>
              <h2>Project phases</h2>
            </Column>
          </Row>
          <Row>
            <Column span={12}>
              <PhaseLifecycle
                dates={projectDates?.dates}
                handleEditDatesClick={() => setShowEditDatesPanel(true)}
                currentUserType={currentUserType}
              />
            </Column>
          </Row>
        </div>
      )}

      {standardSpecificDefinition && hasStandardSpecificDefinition(standardSpecificDefinition) && (
        <div className="ProjectTabSection">
          <StandardSpecificSection
            standardSpecificDefinition={standardSpecificDefinition}
            hasWriteProjectDetailsPermission={isEditable}
            centerTextHidden={false}
          />
        </div>
      )}
      <GenericModal
        show={showUpdateGroupVVBModal}
        onConfirm={() => onSubmit(undefined)}
        closeModal={() => setShowUpdateGroupVVBModal(false)}
        data={{
          title: "Update group vvb",
          text: [
            "All projects in a group must have the same VVB. Since this project is part of a group, by updating the VVB for this project all other projects in the group will be updated with the same VVB. ",
          ],
          confirmText: "Update",
          cancelText: "Cancel",
        }}
        isLoading={isOnSubmitLoading}
      />
      <GenericModal
        show={showStandardChangeModal}
        onConfirm={() => onSubmit(undefined)}
        closeModal={onCancelStandardChange}
        data={{
          title: "Assign standard",
          text: [
            "Are you sure you want to assign this standard to the project? This cannot be undone and will configure the project against the selected governance standard.",
          ],
          confirmText: "Confirm",
          cancelText: "Cancel",
        }}
        isLoading={isOnSubmitLoading}
      />
      {projectDates && (
        <EditDatesPanel
          data={projectDates}
          setData={setProjectDates}
          show={showEditDatesPanel}
          onClose={() => setShowEditDatesPanel(false)}
          canShowCompletePhaseToggle
          shouldRefreshProjectDetails={shouldRefreshProjectDetails}
        />
      )}
    </>
  );
};

export default Overview;
